import { useEffect } from 'react';

const DEFAULT_OG_IMAGE = '/android-chrome-512x512.png';
const SITE_NAME = 'Ali Lee';

function getAbsoluteUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl;
  }
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return pathOrUrl.startsWith('/') ? `${base}${pathOrUrl}` : `${base}/${pathOrUrl}`;
}

function setOrCreateMeta(
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name',
) {
  const selector = attribute === 'property'
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setOrCreateLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  let el = document.querySelector(`link[rel="${rel}"][href]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

export interface MetaProps {
  title: string;
  description?: string;
  /** Path or absolute URL for social sharing image. Defaults to site icon. */
  image?: string;
  /** Path-only canonical (e.g. "/resume"). If not set, uses current pathname. */
  canonicalPath?: string;
  /** Set to true for 404 / no-index pages. */
  noIndex?: boolean;
}

export default function Meta({
  title,
  description = '',
  image = DEFAULT_OG_IMAGE,
  canonicalPath,
  noIndex = false,
}: MetaProps) {
  useEffect(() => {
    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description);
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'description';
      newMeta.content = description;
      document.head.appendChild(newMeta);
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const path = canonicalPath ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
    const canonicalUrl = `${origin}${path}`;
    const imageUrl = getAbsoluteUrl(image);

    // Open Graph
    setOrCreateMeta('og:title', title, 'property');
    setOrCreateMeta('og:description', description, 'property');
    setOrCreateMeta('og:url', canonicalUrl, 'property');
    setOrCreateMeta('og:image', imageUrl, 'property');
    setOrCreateMeta('og:type', 'website', 'property');
    setOrCreateMeta('og:site_name', SITE_NAME, 'property');

    // Twitter Card
    setOrCreateMeta('twitter:card', 'summary_large_image');
    setOrCreateMeta('twitter:title', title);
    setOrCreateMeta('twitter:description', description);
    setOrCreateMeta('twitter:image', imageUrl);

    // Canonical
    setOrCreateLink('canonical', canonicalUrl);

    // Robots
    if (noIndex) {
      setOrCreateMeta('robots', 'noindex, nofollow');
    } else {
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.remove();
    }
  }, [title, description, image, canonicalPath, noIndex]);

  return null;
}
